import type { SimCareerView, SimChoiceResponse, SimEventView, SimMe } from '@cruz/shared';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { CrossMark } from '../components/icons.tsx';
import { load, save } from '../lib/storage.ts';
import { simApi, simErrorMessage } from './api.ts';
import { countdown, daysUntil, euros, num, plural, signed } from './format.ts';
import { ReportButton } from './Report.tsx';
import { ShareButton } from './Share.tsx';
import { card, Notice, primaryButton, secondaryButton, Working } from './ui.tsx';

/** "Hoy": the event of the moment, what happened after choosing, and the end of the day. */

type Step =
  | { step: 'loading' }
  | { step: 'event'; event: SimEventView; choosing: number | null }
  | {
      step: 'outcome';
      event: SimEventView;
      choice: number;
      result: SimChoiceResponse;
      before: SimCareerView;
    }
  | { step: 'done' }
  | { step: 'error'; message: string };

const hookKey = (careerId: string) => `cruz.sim.hook.${careerId}`;

/** Re-renders every second while mounted. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export function TodayTab({
  me,
  career,
  onCareer,
  onNewCareer,
}: {
  me: SimMe;
  career: SimCareerView;
  onCareer: (c: SimCareerView) => void;
  onNewCareer: () => void;
}) {
  const [step, setStep] = useState<Step>({ step: 'loading' });
  const fetchEvent = useCallback(
    () =>
      simApi
        .event()
        .then((r) => {
          onCareer(r.career);
          setStep(r.event ? { step: 'event', event: r.event, choosing: null } : { step: 'done' });
        })
        .catch((err: unknown) => setStep({ step: 'error', message: simErrorMessage(err) })),
    [onCareer],
  );
  useEffect(() => {
    void fetchEvent();
  }, [fetchEvent]);
  const loadEvent = () => {
    setStep({ step: 'loading' });
    void fetchEvent();
  };

  const choose = async (event: SimEventView, choice: number) => {
    setStep({ step: 'event', event, choosing: choice });
    const before = career;
    try {
      const result = await simApi.choose(event.idx, choice);
      if (result.hook) save(hookKey(before.id), { day: before.today.day, text: result.hook });
      onCareer(result.career);
      setStep({ step: 'outcome', event, choice, result, before });
      scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setStep({ step: 'error', message: simErrorMessage(err) });
    }
  };

  return (
    <div className="space-y-4">
      {!me.email_verified && <VerifyReminder />}
      {step.step === 'loading' && <Working label="Viendo qué pasa hoy en tu hermandad…" />}
      {step.step === 'error' && (
        <div className="space-y-3">
          <Notice tone="error">{step.message}</Notice>
          <button type="button" className={secondaryButton} onClick={loadEvent}>
            Volver a probar
          </button>
        </div>
      )}
      {step.step === 'event' && (
        <EventCard
          event={step.event}
          career={career}
          choosing={step.choosing}
          onChoose={(i) => void choose(step.event, i)}
        />
      )}
      {step.step === 'outcome' && (
        <Outcome
          event={step.event}
          choice={step.choice}
          result={step.result}
          before={step.before}
          onNext={() => (step.result.day_done ? setStep({ step: 'done' }) : loadEvent())}
        />
      )}
      {step.step === 'done' &&
        (career.finished ? (
          <Finished career={career} onNewCareer={onNewCareer} />
        ) : (
          <DayDone career={career} onNewDay={loadEvent} />
        ))}
    </div>
  );
}

function VerifyReminder() {
  const [state, setState] = useState<'idle' | 'sent' | 'error'>('idle');
  return (
    <Notice tone="warn">
      <p>Verifica tu correo con el enlace que te mandamos: así no pierdes tu cuenta.</p>
      {state === 'idle' && (
        <button
          type="button"
          className="mt-1 font-semibold text-purple underline decoration-gold underline-offset-4 dark:text-gold-300"
          onClick={() => {
            simApi
              .resendVerification()
              .then(() => setState('sent'))
              .catch(() => setState('error'));
          }}
        >
          Mandármelo otra vez
        </button>
      )}
      {state === 'sent' && <p className="mt-1 font-medium">Enviado. Mira tu correo (y el spam).</p>}
      {state === 'error' && (
        <p className="mt-1 font-medium">Espera un par de minutos y vuelve a probar.</p>
      )}
    </Notice>
  );
}

function DayProgress({ career }: { career: SimCareerView }) {
  const { events_per_day: total, events_left: left } = career.today;
  const current = Math.min(total, total - left + 1);
  return (
    <div className="flex items-center gap-3 text-sm text-ink-soft dark:text-cream/75">
      <span className="flex gap-1.5" aria-hidden>
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className={`h-2 w-6 rounded-full ${i < total - left ? 'bg-gold' : i === total - left ? 'bg-purple dark:bg-gold-300' : 'bg-purple/15 dark:bg-cream/15'}`}
          />
        ))}
      </span>
      <span>
        Hoy, {current} de {total} · {career.time.phase_label} · año {career.time.year}
      </span>
    </div>
  );
}

function EventCard({
  event,
  career,
  choosing,
  onChoose,
}: {
  event: SimEventView;
  career: SimCareerView;
  choosing: number | null;
  onChoose: (i: number) => void;
}) {
  // A new event: screen readers start from its title
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => titleRef.current?.focus({ preventScroll: true }), [event.idx]);
  return (
    <article aria-labelledby="event-title" className="space-y-4">
      <DayProgress career={career} />
      {event.from_yesterday && (
        <p className="rounded-xl border-l-4 border-gold bg-cream-200 px-4 py-2.5 text-[0.95rem] italic dark:bg-gold/10">
          Lo que quedó pendiente ayer: {event.from_yesterday}
        </p>
      )}
      <div className={`${card} px-5 py-5`}>
        <h1
          id="event-title"
          ref={titleRef}
          tabIndex={-1}
          className="outline-none font-serif text-[1.9rem] leading-tight font-semibold text-purple dark:text-gold-300"
        >
          {event.title}
        </h1>
        <p className="mt-3 text-[1.08rem] leading-relaxed whitespace-pre-line">{event.text}</p>
      </div>
      <h2 className="sr-only">¿Qué haces?</h2>
      <ul className="space-y-3">
        {event.options.map((o) => (
          <li key={o.index}>
            <button
              type="button"
              disabled={choosing !== null}
              onClick={() => onChoose(o.index)}
              className={`flex min-h-14 w-full items-start gap-3 rounded-xl border px-4 py-3 text-left text-[1.05rem] leading-snug transition duration-150 active:scale-[0.99] disabled:cursor-default ${
                choosing === o.index
                  ? 'border-gold bg-cream-200 dark:bg-gold/15'
                  : 'border-purple/20 bg-paper hover:border-gold dark:border-cream/20 dark:bg-night-800 dark:hover:border-gold'
              } ${choosing !== null && choosing !== o.index ? 'opacity-50' : ''}`}
            >
              <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-purple font-serif text-sm font-semibold text-cream dark:bg-gold-300 dark:text-night">
                {o.index + 1}
              </span>
              <span>{o.text}</span>
            </button>
          </li>
        ))}
      </ul>
      {choosing !== null ? (
        <Working label="A ver qué pasa…" />
      ) : (
        <ReportButton
          what="este evento"
          onSend={(reason, comment) => simApi.reportEvent(event.idx, reason, comment)}
        />
      )}
    </article>
  );
}

function Delta({
  label,
  delta,
  format = signed,
}: {
  label: string;
  delta: number;
  format?: (n: number) => string;
}) {
  const up = delta > 0;
  return (
    <li
      className={`animate-rise inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${
        up
          ? 'bg-gold/20 text-gold-700 dark:bg-gold/15 dark:text-gold-300'
          : 'bg-burgundy-300/30 text-burgundy dark:bg-burgundy/30 dark:text-burgundy-300'
      }`}
    >
      {label} <span className="tabular">{format(delta)}</span>
    </li>
  );
}

/** Rose petals over a promotion, like the ones thrown at the paso. Decorative only. */
function Petals() {
  // Fixed positions: the same shower every time, no randomness in render
  const petals = [
    [8, 0, 18, 260],
    [22, 350, -14, -220],
    [35, 120, 24, 300],
    [48, 600, -20, 280],
    [61, 220, 16, -320],
    [74, 480, -24, 240],
    [86, 80, 12, 340],
    [15, 820, 20, -260],
    [55, 950, -16, 300],
    [92, 700, -10, -280],
  ] as const;
  return (
    <span className="pointer-events-none absolute inset-0" aria-hidden>
      {petals.map(([left, delay, drift, spin], i) => (
        <span
          key={i}
          className={`animate-petal absolute top-0 h-2.5 w-2 rounded-[60%_10%_60%_10%] ${
            i % 3 === 0 ? 'bg-gold-300' : i % 3 === 1 ? 'bg-burgundy-300' : 'bg-cream/90'
          }`}
          style={
            {
              left: `${left}%`,
              animationDelay: `${delay}ms`,
              '--drift': `${drift}px`,
              '--spin': `${spin}deg`,
            } as CSSProperties
          }
        />
      ))}
    </span>
  );
}

function Outcome({
  event,
  choice,
  result,
  before,
  onNext,
}: {
  event: SimEventView;
  choice: number;
  result: SimChoiceResponse;
  before: SimCareerView;
  onNext: () => void;
}) {
  const c = result.changes;
  const b = c.brotherhood;
  const moments = result.career.timeline.slice(before.timeline.length).filter((t) => t.shareable);
  const promoted = Boolean(c.promoted);
  // The options are gone: screen readers go on from what happened
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => titleRef.current?.focus({ preventScroll: true }), []);
  // A promotion is felt in the hand too (phones that can vibrate)
  useEffect(() => {
    if (
      promoted &&
      typeof matchMedia === 'function' &&
      !matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      navigator.vibrate?.([40, 60, 40]);
  }, [promoted]);
  const signedEuros = (n: number) => `${n > 0 ? '+' : '−'}${euros(Math.abs(n))}`;
  return (
    <article aria-labelledby="outcome-title" className="space-y-4">
      {/* The big news first, in sight without scrolling */}
      {c.promoted && (
        <div
          role="status"
          className="animate-crown relative overflow-hidden rounded-2xl border-2 border-gold bg-purple px-5 py-6 text-center text-cream dark:bg-night-700"
        >
          <Petals />
          <span className="relative mx-auto block w-fit text-gold-300">
            <CrossMark size={30} />
          </span>
          <p className="relative mt-2 text-xs font-semibold tracking-[0.2em] text-gold-300 uppercase">
            ¡Ascenso!
          </p>
          <p className="relative mt-1 font-serif text-[2rem] leading-tight font-semibold">
            {c.promoted.rank_label}
          </p>
          {c.promoted.post && (
            <p className="relative mt-1 text-lg text-cream/85">{c.promoted.post}</p>
          )}
        </div>
      )}
      <div className={`${card} px-5 py-5`}>
        <p className="text-sm text-ink-soft dark:text-cream/70">{event.title}</p>
        <p className="mt-1 font-medium">Elegiste: {event.options[choice]?.text}</p>
        <h1 id="outcome-title" ref={titleRef} tabIndex={-1} className="sr-only outline-none">
          Lo que pasó
        </h1>
        <p className="mt-3 text-[1.08rem] leading-relaxed whitespace-pre-line">{result.outcome}</p>
        {(c.stats.length > 0 || c.relation || b.members || b.prestige || b.treasury) && (
          <ul aria-label="Cambios" className="mt-4 flex flex-wrap gap-2">
            {c.stats.map((s) => (
              <Delta key={s.key} label={s.label} delta={s.delta} />
            ))}
            {c.relation && (
              <Delta label={`Relación con ${c.relation.name}`} delta={c.relation.delta} />
            )}
            {b.members !== 0 && <Delta label="Hermanos" delta={b.members} />}
            {b.prestige !== 0 && <Delta label="Prestigio de la hermandad" delta={b.prestige} />}
            {b.treasury !== 0 && <Delta label="Caja" delta={b.treasury} format={signedEuros} />}
          </ul>
        )}
      </div>

      {c.year_ended && (
        <Notice>Se cierra el año cofrade. Sumas un año de antigüedad y la nómina se mueve.</Notice>
      )}
      {moments.map((m) => (
        <div
          key={m.index}
          className={`${card} flex flex-wrap items-center justify-between gap-3 px-4 py-3`}
        >
          <p className="font-medium">{m.text}</p>
          <ShareButton request={{ kind: 'moment', ref: String(m.index) }} />
        </div>
      ))}
      {c.achievements.map((a) => (
        <div
          key={a.id}
          className="animate-rise rounded-2xl border border-gold bg-cream-200 px-5 py-4 dark:bg-gold/10"
        >
          <p className="text-xs font-semibold tracking-[0.16em] text-gold-700 uppercase dark:text-gold-300">
            Logro conseguido
          </p>
          <p className="mt-1 font-serif text-2xl font-semibold">{a.name}</p>
          <p className="text-ink-soft dark:text-cream/80">{a.description}</p>
          {a.shareable && (
            <div className="mt-3">
              <ShareButton request={{ kind: 'achievement', ref: a.id }} />
            </div>
          )}
        </div>
      ))}

      {result.hook && (
        <div className="rounded-2xl border-l-4 border-burgundy bg-paper px-5 py-4 dark:border-burgundy-300 dark:bg-night-800">
          <p className="text-xs font-semibold tracking-[0.16em] text-burgundy uppercase dark:text-burgundy-300">
            Mañana…
          </p>
          <p className="mt-1 text-lg italic">{result.hook}</p>
        </div>
      )}
      <button type="button" className={primaryButton} onClick={onNext}>
        {result.day_done ? 'Terminar el día' : 'Siguiente'}
      </button>
      <ReportButton
        what="este evento"
        onSend={(reason, comment) => simApi.reportEvent(event.idx, reason, comment)}
      />
    </article>
  );
}

function DayDone({ career, onNewDay }: { career: SimCareerView; onNewDay: () => void }) {
  const now = useNow();
  const hook = load<{ day: string; text: string } | null>(hookKey(career.id), null);
  const next = career.today.next_day_at;
  const left = next ? new Date(next).getTime() - now : 0;
  const hw = career.today.holy_week;
  const days = daysUntil(hw.starts_at, now);
  const t = career.time;
  return (
    <div className="space-y-4">
      <div className={`${card} px-5 py-5 text-center`}>
        <h1 className="text-xs font-semibold tracking-[0.16em] text-gold-700 uppercase dark:text-gold-300">
          Por hoy, esto es todo
        </h1>
        {hook && hook.day === career.today.day ? (
          <p className="mt-3 font-serif text-2xl leading-snug italic">{hook.text}</p>
        ) : (
          <p className="mt-3 font-serif text-2xl leading-snug">
            Tu hermandad sigue su vida. Mañana, más.
          </p>
        )}
        <div className="mt-5 border-t border-gold/40 pt-4">
          {left > 0 ? (
            <p className="text-lg">
              Mañana a las 6:00 sigue tu historia: quedan{' '}
              <strong className="tabular">{countdown(left)}</strong>.
            </p>
          ) : (
            <button type="button" className={primaryButton} onClick={onNewDay}>
              Empezar el día
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className={`${card} px-4 py-3`}>
          <p className="text-sm text-ink-soft dark:text-cream/70">Racha</p>
          <p className="font-serif text-2xl font-semibold">{plural(t.streak, 'día', 'días')}</p>
          <p className="text-xs text-ink-soft dark:text-cream/60">
            Mejor: {plural(t.best_streak, 'día', 'días')}
          </p>
        </div>
        <div className={`${card} px-4 py-3`}>
          <p className="text-sm text-ink-soft dark:text-cream/70">Semana Santa de verdad</p>
          <p className="font-serif text-2xl font-semibold">
            {hw.on_now ? '¡Ya está aquí!' : days === 1 ? 'Mañana' : `Faltan ${num(days)} días`}
          </p>
        </div>
      </div>
      <div className="space-y-3">
        <p className="text-center text-ink-soft dark:text-cream/75">
          Mientras tanto, en Cruz de IA:
        </p>
        <a href="/" className={secondaryButton}>
          Pregunta por la Semana Santa
        </a>
        <a href="/reto/nuevo" className={secondaryButton}>
          Reta a tus amigos
        </a>
      </div>
    </div>
  );
}

function Finished({ career, onNewCareer }: { career: SimCareerView; onNewCareer: () => void }) {
  const end = [...career.timeline].reverse().find((t) => t.kind === 'end' && t.shareable);
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border-2 border-gold bg-purple px-5 py-6 text-center text-cream dark:bg-night-700">
        <p className="text-xs font-semibold tracking-[0.16em] text-gold-300 uppercase">
          Fin de tu carrera
        </p>
        <p className="mt-2 font-serif text-3xl font-semibold">{career.player.rank_label}</p>
        <p className="mt-2">
          {plural(career.player.seniority_years, 'año', 'años')} de hermano ·{' '}
          {plural(career.player.processions, 'estación de penitencia', 'estaciones de penitencia')}
        </p>
        {end && (
          <div className="mt-4 flex justify-center">
            <ShareButton
              request={{ kind: 'moment', ref: String(end.index) }}
              label="Compartir mi carrera"
            />
          </div>
        )}
      </div>
      <button type="button" className={primaryButton} onClick={onNewCareer}>
        Empezar otra carrera
      </button>
    </div>
  );
}
